import { useState, type ReactNode } from "react";
import {
  ArrowDown,
  ArrowLeft,
  Check,
  Copy,
  Download,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useSeo } from "~/lib/seo/useSeo";
import {
  ASSETS,
  CABECUDO_PROMPT,
  CODEX_PROMPT,
  GUIDE_URL,
  KLING_URL,
  NARRATION,
  SETTINGS,
} from "~/lib/tutorials/cabecudo";

function CopyText({ text, label }: { text: string; label: string }) {
  const [status, setStatus] = useState<"idle" | "copied" | "error">("idle");
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setStatus("copied");
    } catch {
      setStatus("error");
    }
  }
  return (
    <div>
      <button type="button" onClick={copy} className="tutorial-button">
        {status === "copied" ? (
          <Check size={17} aria-hidden="true" />
        ) : (
          <Copy size={17} aria-hidden="true" />
        )}
        {label}
      </button>
      <p role="status" className="mt-2 text-sm text-jg-fg-muted">
        {status === "copied"
          ? "Copiado! Agora cole no campo de texto."
          : status === "error"
            ? "Não foi possível copiar. Selecione o texto abaixo e use Copiar no seu aparelho."
            : " "}
      </p>
    </div>
  );
}

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <section
      id={`passo-${number}`}
      className="tutorial-step scroll-mt-24 border-t border-jg-border py-9"
    >
      <div className="mb-5 flex items-start gap-4">
        <span className="tutorial-step-number">
          {String(number).padStart(2, "0")}
        </span>
        <h3 className="font-display text-2xl font-semibold leading-tight sm:text-3xl">
          {title}
        </h3>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-jg-fg-muted">
        {children}
      </div>
    </section>
  );
}

function External({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="tutorial-inline-link"
    >
      {children}
      <ExternalLink size={14} aria-hidden="true" />
      <span className="sr-only"> (abre em outra aba)</span>
    </a>
  );
}

export function TutorialCabecudo() {
  useSeo({
    title: "Cabeçudo: troque uma pessoa do vídeo com IA",
    description:
      "Tutorial completo e simples do Kling 3.0 Omni: antes e depois, arquivos para baixar, prompt pronto, configurações e roteiro para gravar.",
    pathname: "/tutorial/cabecudo",
  });
  return (
    <article
      lang="pt-BR"
      className="tutorial-page mx-auto max-w-6xl px-5 py-10 sm:px-8 sm:py-16"
    >
      <Link
        to="/tutorial"
        className="mb-8 inline-flex items-center gap-2 text-sm text-jg-fg-muted hover:text-jg-fg"
      >
        <ArrowLeft size={16} aria-hidden="true" /> Todos os tutoriais
      </Link>
      <header className="grid items-center gap-9 md:grid-cols-[1.15fr_.85fr]">
        <div>
          <p className="tutorial-eyebrow">Tutorial 01 · Kling · do zero</p>
          <h1 className="mt-4 font-display text-5xl font-semibold tracking-tight sm:text-7xl">
            Cabeçudo<span className="text-jg-ai">.</span>
          </h1>
          <p className="mt-5 max-w-xl text-xl leading-relaxed">
            Uma pessoa sai. Seu personagem entra.
          </p>
          <p className="mt-4 max-w-xl leading-relaxed text-jg-fg-muted">
            Aprenda a trocar a mulher do vídeo pelo personagem de terno, cabelo
            loiro e bigode. Você só precisa de um vídeo, duas imagens e o texto
            pronto desta página.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="#passo-1" className="tutorial-button">
              Começar <ArrowDown size={17} aria-hidden="true" />
            </a>
            <a
              href={`${ASSETS}/kit-cabecudo-imagens-textos.zip`}
              download
              className="tutorial-button tutorial-button-secondary"
            >
              <Download size={17} aria-hidden="true" /> Baixar imagens e textos
            </a>
          </div>
          <p className="mt-4 text-sm text-jg-fg-muted">
            Sem programação · leitura de cerca de 10 min · geração paga no Kling
          </p>
        </div>
        <figure className="flex justify-center gap-3 overflow-hidden rounded-2xl bg-[#c3c3c0] p-5">
          <img
            src={`${ASSETS}/personagem-corpo.webp`}
            alt="Referência de corpo inteiro do Cabeçudo, com terno marrom e sapatos"
            width="864"
            height="1152"
            className="h-80 w-auto rounded-lg object-contain"
          />
          <img
            src={`${ASSETS}/personagem-rosto.webp`}
            alt="Detalhe do rosto, bigode loiro e cabelo do personagem"
            width="511"
            height="511"
            className="mt-5 hidden h-36 w-36 rounded-full object-cover lg:block"
          />
        </figure>
      </header>

      <section aria-labelledby="resultado-titulo" className="mt-14">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="tutorial-eyebrow">O que você vai fazer</p>
            <h2
              id="resultado-titulo"
              className="mt-2 font-display text-2xl font-semibold sm:text-3xl"
            >
              Veja a troca na prática
            </h2>
          </div>
          <p className="text-sm text-jg-fg-muted">
            Dê play para comparar. Os vídeos são apenas exemplos.
          </p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          {[
            ["Antes · vídeo original", "video-original", "antes"],
            ["Depois · resultado aprovado", "resultado", "depois"],
          ].map(([label, file, poster]) => (
            <figure
              key={file}
              className="overflow-hidden rounded-xl border border-jg-border bg-jg-bg-soft"
            >
              <video
                controls
                controlsList="nodownload"
                playsInline
                preload="none"
                poster={`${ASSETS}/${poster}.webp`}
                aria-label={label}
                className="aspect-[9/16] max-h-[480px] w-full bg-black object-contain"
              >
                <source src={`${ASSETS}/${file}.mp4`} type="video/mp4" />
                Seu navegador não reproduz este vídeo. Tente outro navegador.
              </video>
              <figcaption className="p-4 text-sm font-medium">
                {label}
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-4 text-sm leading-relaxed text-jg-fg-muted">
          Exemplo real feito em 8 de outubro de 2026. Foi enviado pelo Codex ao
          Kling via conexão MCP, com VIDEO 3.0 Omni, 1080p e duas referências. A
          geração custou 108 créditos. Abaixo você aprende a fazer pelo
          navegador; no final há também o pedido pronto para o Codex.
        </p>
      </section>

      <div className="mt-12 grid items-start gap-10 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="lg:sticky lg:top-24">
          <nav
            aria-label="Índice do tutorial"
            className="rounded-xl border border-jg-border p-5"
          >
            <p className="mb-3 font-medium">Seu caminho</p>
            <ol className="space-y-3 text-sm text-jg-fg-muted">
              {[
                "Preparar os arquivos",
                "Abrir o Kling",
                "Anexar as referências",
                "Copiar o texto",
                "Ajustar as opções",
                "Gerar e aguardar",
              ].map((title, i) => (
                <li key={title}>
                  <a href={`#passo-${i + 1}`} className="hover:text-jg-ai">
                    {i + 1}. {title}
                  </a>
                </li>
              ))}
            </ol>
            <a
              href="#roteiro"
              className="mt-5 block border-t border-jg-border pt-4 text-sm text-jg-ai"
            >
              Roteiro para gravar →
            </a>
          </nav>
        </aside>

        <div className="min-w-0">
          <section className="mb-8 rounded-xl border border-jg-ai/25 bg-jg-ai/5 p-5">
            <h2 className="font-display text-xl font-semibold">
              Primeiro, entenda a ideia
            </h2>
            <p className="mt-3 leading-relaxed text-jg-fg-muted">
              O vídeo ensina{" "}
              <strong className="text-jg-fg">a cena e os movimentos</strong>. A
              foto de corpo inteiro ensina{" "}
              <strong className="text-jg-fg">a roupa e a aparência</strong>. O
              retrato ensina{" "}
              <strong className="text-jg-fg">o rosto e o cabelo</strong>. O
              prompt é apenas o texto que explica o que a IA deve trocar.
            </p>
            <p className="mt-3 text-sm text-jg-fg-muted">
              Usamos edição de vídeo no Omni. Para repetir este exemplo, siga
              essa área do Kling. Não é preciso instalar programa, escrever
              código ou configurar uma conexão MCP.
            </p>
          </section>

          <Step number={1} title="Prepare seu vídeo e baixe as duas imagens">
            <p>
              Crie uma pasta chamada{" "}
              <strong className="text-jg-fg">Cabeçudo</strong>. Coloque nela um
              vídeo seu e as duas imagens abaixo. Os vídeos de antes e depois
              desta página servem apenas para mostrar o exemplo e não fazem
              parte do kit para baixar.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                [
                  "1. Corpo inteiro",
                  "personagem-corpo.jpg",
                  "Mostra o terno e os sapatos.",
                ],
                [
                  "2. Retrato do rosto",
                  "personagem-rosto.png",
                  "Mostra o bigode e o cabelo.",
                ],
              ].map(([title, file, note]) => (
                <a
                  key={file}
                  href={`${ASSETS}/${file}`}
                  download
                  className="rounded-lg border border-jg-border p-4 hover:border-jg-ai"
                >
                  <Download
                    size={19}
                    className="mb-3 text-jg-ai"
                    aria-hidden="true"
                  />
                  <strong className="block text-sm text-jg-fg">{title}</strong>
                  <span className="mt-2 block text-sm">{note}</span>
                </a>
              ))}
            </div>
            <p>
              Para baixar as duas imagens e os textos juntos, use{" "}
              <a
                href={`${ASSETS}/kit-cabecudo-imagens-textos.zip`}
                download
                className="tutorial-inline-link"
              >
                Baixar imagens e textos
              </a>{" "}
              e escolha “Extrair” ou “Descompactar” depois do download. O kit
              inclui o prompt, o roteiro e este guia, sem vídeos.
            </p>
            <p className="tutorial-note">
              Escolha um vídeo seu de 3 a 10 segundos, com a pessoa bem visível.
              Grave na vertical para Reels ou Shorts. Use vídeos de pessoas que
              autorizaram o uso. Você também pode usar duas imagens de outro
              personagem, uma de corpo inteiro e outra do rosto.
            </p>
          </Step>

          <Step number={2} title="Abra o Kling e escolha Omni">
            <p>
              Abra <External href={KLING_URL}>Kling · área Omni</External> e
              entre na sua conta. Se aparecer a página inicial, toque em{" "}
              <strong className="text-jg-fg">Omni</strong> no menu. No celular,
              o menu pode estar recolhido.
            </p>
            <p>
              No seletor de modelo, escolha{" "}
              <strong className="text-jg-fg">VIDEO 3.0 Omni</strong>. Esse é o
              modelo usado no exemplo aprovado.
            </p>
            <p>
              Você precisa ter créditos disponíveis para gerar. Confira o saldo
              da sua própria conta. Se o modelo não estiver disponível, veja o
              que sua conta oferece antes de continuar.
            </p>
            <p className="tutorial-note">
              Os nomes dos botões podem aparecer em inglês e mudar com
              atualizações. “Omni” é a área; “VIDEO 3.0 Omni” é o modelo. O{" "}
              <External href={GUIDE_URL}>guia oficial do modelo</External> ajuda
              a localizar a interface atual.
            </p>
          </Step>

          <Step number={3} title="Anexe o vídeo e as duas fotos">
            <ol className="tutorial-list">
              <li>
                Na área de anexos, toque no botão{" "}
                <strong className="text-jg-fg">
                  + / Upload / Click, Drop, Paste
                </strong>{" "}
                e selecione{" "}
                <strong className="text-jg-fg">
                  o vídeo que você escolheu
                </strong>
                .
              </li>
              <li>
                Anexe{" "}
                <strong className="text-jg-fg">personagem-corpo.jpg</strong>{" "}
                como a primeira imagem.
              </li>
              <li>
                Anexe{" "}
                <strong className="text-jg-fg">personagem-rosto.png</strong>{" "}
                como a segunda imagem.
              </li>
              <li>
                Aguarde o envio terminar e confira:{" "}
                <strong className="text-jg-fg">1 vídeo + 2 imagens</strong>{" "}
                visíveis.
              </li>
            </ol>
            <p>
              Se a tela oferecer uma escolha entre usar o vídeo como base para
              editar ou apenas como inspiração, escolha{" "}
              <strong className="text-jg-fg">
                editar / transformar o vídeo existente
              </strong>
              . A cena e os movimentos precisam vir do vídeo original.
            </p>
            <div className="rounded-lg bg-jg-bg-soft p-4 text-sm">
              <p className="font-medium text-jg-fg">
                Como indicar os anexos no texto
              </p>
              <ul className="mt-3 space-y-2">
                <li>
                  <strong>@Video1</strong> = o primeiro vídeo, a cena original.
                </li>
                <li>
                  <strong>@Image1</strong> = a primeira imagem, o corpo inteiro.
                </li>
                <li>
                  <strong>@Image2</strong> = a segunda imagem, o retrato.
                </li>
              </ul>
              <p className="mt-3">
                Estes nomes mostram a ordem dos arquivos. No campo do Kling, use
                o seletor de referências: digite @ e escolha o anexo
                correspondente. Substitua cada nome do texto pela referência que
                o Kling inserir. O nome exibido pode variar; o importante é
                selecionar o vídeo e as imagens corretas.
              </p>
            </div>
          </Step>

          <Step number={4} title="Copie e cole o texto pronto">
            <p>
              Toque em <strong className="text-jg-fg">Copiar prompt</strong> e
              cole no campo de descrição do Kling. No celular, mantenha o dedo
              pressionado no campo e escolha “Colar”. No computador, use Ctrl +
              V (ou Command + V no Mac).
            </p>
            <p>
              O texto abaixo é uma adaptação do pedido usado neste exemplo, com
              nomes de referência em inglês. Copie e cole; depois selecione os
              anexos como explicado no passo anterior. Antes de gerar, ajuste a
              descrição da pessoa e do cenário para o seu próprio vídeo.
            </p>
            <CopyText text={CABECUDO_PROMPT} label="Copiar prompt" />
            <pre className="tutorial-copy-block">{CABECUDO_PROMPT}</pre>
            <a
              href={`${ASSETS}/prompt-referencias.txt`}
              download
              className="tutorial-inline-link"
            >
              Baixar o prompt em arquivo de texto{" "}
              <Download size={15} aria-hidden="true" />
            </a>
            <p className="tutorial-note">
              “A mulher de cabelo curto sentada à esquerda, atrás do homem”
              descreve apenas o exemplo. No seu vídeo, diga quem deve ser
              substituído pela posição e pela roupa. Adapte também os trechos
              sobre o homem de vermelho, os gatos e a sala ao que deve
              permanecer na sua cena. Se usar outro personagem, atualize a
              roupa, o rosto e o cabelo no texto.
            </p>
          </Step>

          <Step number={5} title="Confira as opções antes de gastar créditos">
            <p>
              Abra as opções próximas ao botão Gerar. Use esta lista como
              referência:
            </p>
            <div className="overflow-x-auto rounded-lg border border-jg-border">
              <table className="w-full text-left text-sm">
                <caption className="sr-only">
                  Configurações usadas no vídeo Cabeçudo
                </caption>
                <thead className="bg-jg-bg-soft text-jg-fg">
                  <tr>
                    <th scope="col" className="p-4">
                      Opção
                    </th>
                    <th scope="col" className="p-4">
                      O que escolher
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {SETTINGS.map(([name, value]) => (
                    <tr key={name} className="border-t border-jg-border">
                      <th scope="row" className="p-4 font-medium text-jg-fg">
                        {name}
                      </th>
                      <td className="p-4">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>
              Para esta edição, mantenha o formato do vídeo original. Se
              precisar escolher uma proporção, o vídeo de exemplo é vertical:{" "}
              <strong className="text-jg-fg">9:16</strong>. A edição pode seguir
              automaticamente a duração do arquivo base; não corte o final só
              para arredondar para 9 segundos.
            </p>
            <p>
              Se houver duas opções de som,{" "}
              <strong className="text-jg-fg">manter o som original</strong> e{" "}
              <strong className="text-jg-fg">gerar som novo</strong> são coisas
              diferentes. Queremos a primeira. Se a interface não oferecer a
              opção de áudio original, confira o resultado antes de publicar.
            </p>
            <p className="tutorial-note">
              <strong className="text-jg-fg">
                Custo deste exemplo: 108 créditos.
              </strong>{" "}
              Esse foi o valor cobrado pela geração em 08/10/2026, não uma
              tabela de preço garantida. O botão Gerar mostra o custo atual da
              sua conta. Uma nova tentativa pode consumir créditos novamente. A
              espera na fila não está incluída no tempo de leitura do tutorial.
            </p>
          </Step>

          <Step number={6} title="Clique em Gerar e aguarde">
            <p>
              Confira os três anexos e o custo. Toque em{" "}
              <strong className="text-jg-fg">Generate / Gerar</strong> uma vez.
              “Queueing” significa que o pedido está na fila; “Generating”
              significa que o vídeo está sendo produzido.
            </p>
            <p>
              Você pode acompanhar pelo histórico ou pela área{" "}
              <strong className="text-jg-fg">Assets / Criações</strong>. A
              espera depende da fila do serviço. Não clique em Gerar de novo só
              porque está demorando: isso pode criar outro pedido pago.
            </p>
          </Step>

          <section
            className="border-t border-jg-border py-9"
            aria-labelledby="ajuda"
          >
            <h2 id="ajuda" className="mb-5 font-display text-2xl font-semibold">
              Se algo sair errado
            </h2>
            <div className="space-y-3">
              {[
                [
                  "Trocou a pessoa errada",
                  "Confira se o vídeo correto foi anexado. No texto, identifique a pessoa pela posição e pela roupa. Neste exemplo, é a mulher sentada à esquerda, atrás do homem de camiseta vermelha.",
                ],
                [
                  "O rosto ou o cabelo mudaram",
                  "Confira a ordem das fotos: corpo inteiro primeiro, retrato depois. Use referências nítidas e do mesmo personagem. A IA pode variar; só gere novamente depois de revisar o pedido e o novo custo.",
                ],
                [
                  "O cenário ou os movimentos mudaram",
                  "Use o vídeo como base para edição, não apenas como inspiração. Mantenha a instrução de preservar câmera, ambiente, gestos e duração. Não divida este clipe curto em várias gerações.",
                ],
                [
                  "Ficou sem o áudio original",
                  "Confira a opção Keep original sound. Se o resultado veio mudo, o áudio do arquivo original pode ser recolocado em um editor de vídeo; pedir áudio novo não recupera a gravação original.",
                ],
                [
                  "O envio falhou ou o modelo não aparece",
                  "Confira conexão, formato MP4 do vídeo e JPG/PNG das referências. Comece por um vídeo de 3 a 10 segundos. Consulte o guia oficial e as opções disponíveis na sua conta antes de pagar por outro pedido.",
                ],
              ].map(([title, answer]) => (
                <details
                  key={title}
                  className="rounded-lg border border-jg-border p-4"
                >
                  <summary className="cursor-pointer font-medium text-jg-fg">
                    {title}
                  </summary>
                  <p className="mt-3 leading-relaxed text-jg-fg-muted">
                    {answer}
                  </p>
                </details>
              ))}
            </div>
          </section>

          <section
            id="roteiro"
            className="scroll-mt-24 border-t border-jg-border py-9"
          >
            <p className="tutorial-eyebrow">Também pronto para você</p>
            <h2 className="mt-3 font-display text-2xl font-semibold">
              Roteiro para gravar a explicação
            </h2>
            <p className="my-4 leading-relaxed text-jg-fg-muted">
              Grave a tela seguindo estas cenas e leia as falas. A duração de
              cerca de 2min25 é uma sugestão para a explicação, não o tempo de
              geração do Kling.
            </p>
            <CopyText text={NARRATION} label="Copiar roteiro" />
            <details className="mt-3 rounded-lg border border-jg-border p-4">
              <summary className="cursor-pointer font-medium">
                Ler o roteiro completo
              </summary>
              <pre className="tutorial-copy-block mt-4">{NARRATION}</pre>
            </details>
            <a
              href={`${ASSETS}/roteiro-video-proprio.txt`}
              download
              className="tutorial-inline-link mt-4"
            >
              Baixar roteiro-video-proprio.txt{" "}
              <Download size={15} aria-hidden="true" />
            </a>
          </section>

          <section className="border-t border-jg-border py-9">
            <h2 className="font-display text-2xl font-semibold">
              Se você já usa Codex com o Kling conectado
            </h2>
            <p className="my-4 leading-relaxed text-jg-fg-muted">
              Foi assim que enviamos o exemplo aprovado: anexamos o vídeo e as
              duas imagens na conversa, e o Codex enviou o pedido ao Kling pela
              conexão MCP. MCP é a conexão que permite ao assistente usar sua
              conta do Kling. Esta alternativa pressupõe que ela já esteja
              configurada; você pode fazer todo o tutorial acima apenas pelo
              navegador.
            </p>
            <p className="mb-4 leading-relaxed text-jg-fg-muted">
              Anexe seu vídeo e as duas imagens. No pedido abaixo, ajuste a
              descrição da pessoa e do cenário ao seu vídeo antes de enviar.
              Confira a conta e os créditos que serão utilizados.
            </p>
            <CopyText text={CODEX_PROMPT} label="Copiar pedido para o Codex" />
            <pre className="tutorial-copy-block">{CODEX_PROMPT}</pre>
          </section>

          <section className="rounded-xl bg-jg-bg-soft p-6">
            <h2 className="font-display text-xl font-semibold">
              Tudo em um lugar
            </h2>
            <p className="mt-3 leading-relaxed text-jg-fg-muted">
              O kit contém as duas imagens do personagem, o prompt, o roteiro e
              um guia em texto. Use um vídeo seu para seguir os passos. Os
              vídeos de exemplo ficam apenas nos players desta página.
            </p>
            <a
              href={`${ASSETS}/kit-cabecudo-imagens-textos.zip`}
              download
              className="tutorial-button mt-5"
            >
              <Download size={17} aria-hidden="true" /> Baixar kit Cabeçudo
            </a>
            <div className="mt-6 flex flex-wrap gap-5 text-sm">
              <External href={KLING_URL}>Abrir Kling Omni</External>
              <External href={GUIDE_URL}>Guia oficial</External>
              <a
                href={`${ASSETS}/guia-video-proprio.txt`}
                download
                className="tutorial-inline-link"
              >
                Guia para salvar ou imprimir
              </a>
            </div>
          </section>
        </div>
      </div>
    </article>
  );
}
